import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, User, Mail, Calendar, Save, Upload, Folder, CheckCircle2, MessageSquare, File, RefreshCw, History } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminJobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [downloading, setDownloading] = useState({});
  
  // Retouched files upload state
  const [retouchedFiles, setRetouchedFiles] = useState([]);
  const [uploadingRetouched, setUploadingRetouched] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  
  // Revised files upload state
  const [revisedFiles, setRevisedFiles] = useState([]);
  const [uploadingRevised, setUploadingRevised] = useState(false);
  const [revisedUploadProgress, setRevisedUploadProgress] = useState({});
  const [revisionNotes, setRevisionNotes] = useState('');
  const revisedFileInputRef = useRef(null);
  const revisedFolderInputRef = useRef(null);

  useEffect(() => {
    fetchJobDetail();
  }, [jobId]);

  const fetchJobDetail = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.get(`${API}/admin/test-jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJob(data);
      setStatus(data.status);
      setAdminNotes(data.admin_notes || '');
    } catch (error) {
      toast.error('Failed to load job details');
      navigate('/admin/test-jobs');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file, index, type = 'original') => {
    const key = `${type}-${index}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.get(
        `${API}/admin/test-jobs/${jobId}/download-url`,
        {
          params: { s3_key: file.s3_key, file_name: file.file_name },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.open(data.download_url, '_blank');
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const updateStatus = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/test-jobs/${jobId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchJobDetail();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const saveAdminNotes = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/test-jobs/${jobId}/notes`,
        { admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notes saved successfully');
      fetchJobDetail();
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setUpdating(false);
    }
  };

  // Get files from directory recursively (for drag & drop)
  const getFilesFromDirectory = async (dirEntry, path = '') => {
    const files = [];
    const dirReader = dirEntry.createReader();
    
    const readEntries = () => {
      return new Promise((resolve, reject) => {
        dirReader.readEntries((entries) => resolve(entries), reject);
      });
    };

    let entries = await readEntries();
    while (entries.length > 0) {
      for (const entry of entries) {
        if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          file.relativePath = path ? `${path}/${entry.name}` : entry.name;
          files.push(file);
        } else if (entry.isDirectory) {
          const subPath = path ? `${path}/${entry.name}` : entry.name;
          const subFiles = await getFilesFromDirectory(entry, subPath);
          files.push(...subFiles);
        }
      }
      entries = await readEntries();
    }
    return files;
  };

  // Retouched files handlers
  const handleRetouchedFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    selectedFiles.forEach(f => {
      f.relativePath = f.webkitRelativePath || f.name;
    });
    await uploadRetouchedFiles(selectedFiles);
    e.target.value = '';
  };

  const handleRetouchedDrop = async (e) => {
    e.preventDefault();
    const items = e.dataTransfer.items;
    const allFiles = [];

    if (items) {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }

      for (const entry of entries) {
        if (entry.isDirectory) {
          toast.info(`Processing folder: ${entry.name}...`);
          const folderFiles = await getFilesFromDirectory(entry, entry.name);
          allFiles.push(...folderFiles);
        } else if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          file.relativePath = file.name;
          allFiles.push(file);
        }
      }
    } else {
      const droppedFiles = Array.from(e.dataTransfer.files);
      droppedFiles.forEach(f => { f.relativePath = f.name; });
      allFiles.push(...droppedFiles);
    }

    if (allFiles.length > 0) {
      await uploadRetouchedFiles(allFiles);
    }
  };

  const uploadRetouchedFiles = async (filesToUpload) => {
    setUploadingRetouched(true);
    const uploadedFiles = [];
    const token = localStorage.getItem('admin_token');

    const batchSize = 3;
    for (let i = 0; i < filesToUpload.length; i += batchSize) {
      const batch = filesToUpload.slice(i, i + batchSize);
      
      await Promise.all(batch.map(async (file) => {
        const displayName = file.relativePath || file.name;
        try {
          const { data } = await axios.post(`${API}/admin/generate-retouched-upload-url`, {
            filename: file.name,
            file_size: file.size,
            content_type: file.type || 'application/octet-stream',
            relative_path: file.relativePath || null
          }, { headers: { Authorization: `Bearer ${token}` } });

          await axios.put(data.upload_url, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [displayName]: progress }));
            }
          });

          uploadedFiles.push({
            file_name: file.name,
            file_size: file.size,
            s3_key: data.s3_key,
            relative_path: file.relativePath || file.name
          });
        } catch (error) {
          console.error(`Failed to upload ${displayName}:`, error);
        }
      }));
    }

    if (uploadedFiles.length > 0) {
      setRetouchedFiles(prev => [...prev, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    }

    setUploadingRetouched(false);
    setUploadProgress({});
  };

  const saveRetouchedFiles = async () => {
    if (retouchedFiles.length === 0) {
      toast.error('No files to save');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/test-jobs/${jobId}/retouched-files`,
        { job_id: jobId, files: retouchedFiles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Retouched files saved and client notified');
      setRetouchedFiles([]);
      fetchJobDetail();
    } catch (error) {
      toast.error('Failed to save retouched files');
    } finally {
      setUpdating(false);
    }
  };

  // Revised files handlers
  const handleRevisedFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    selectedFiles.forEach(f => {
      f.relativePath = f.webkitRelativePath || f.name;
    });
    await uploadRevisedFiles(selectedFiles);
    e.target.value = '';
  };

  const handleRevisedDrop = async (e) => {
    e.preventDefault();
    const items = e.dataTransfer.items;
    const allFiles = [];

    if (items) {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }

      for (const entry of entries) {
        if (entry.isDirectory) {
          toast.info(`Processing folder: ${entry.name}...`);
          const folderFiles = await getFilesFromDirectory(entry, entry.name);
          allFiles.push(...folderFiles);
        } else if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          file.relativePath = file.name;
          allFiles.push(file);
        }
      }
    } else {
      const droppedFiles = Array.from(e.dataTransfer.files);
      droppedFiles.forEach(f => { f.relativePath = f.name; });
      allFiles.push(...droppedFiles);
    }

    if (allFiles.length > 0) {
      await uploadRevisedFiles(allFiles);
    }
  };

  const uploadRevisedFiles = async (filesToUpload) => {
    setUploadingRevised(true);
    const uploadedFiles = [];
    const token = localStorage.getItem('admin_token');

    const batchSize = 3;
    for (let i = 0; i < filesToUpload.length; i += batchSize) {
      const batch = filesToUpload.slice(i, i + batchSize);
      
      await Promise.all(batch.map(async (file) => {
        const displayName = file.relativePath || file.name;
        try {
          const { data } = await axios.post(`${API}/admin/generate-revised-upload-url`, {
            filename: file.name,
            file_size: file.size,
            content_type: file.type || 'application/octet-stream',
            relative_path: file.relativePath || null
          }, { headers: { Authorization: `Bearer ${token}` } });

          await axios.put(data.upload_url, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setRevisedUploadProgress(prev => ({ ...prev, [displayName]: progress }));
            }
          });

          uploadedFiles.push({
            file_name: file.name,
            file_size: file.size,
            s3_key: data.s3_key,
            relative_path: file.relativePath || file.name
          });
        } catch (error) {
          console.error(`Failed to upload ${displayName}:`, error);
        }
      }));
    }

    if (uploadedFiles.length > 0) {
      setRevisedFiles(prev => [...prev, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} revised file(s) uploaded`);
    }

    setUploadingRevised(false);
    setRevisedUploadProgress({});
  };

  const saveRevisedFiles = async () => {
    if (revisedFiles.length === 0) {
      toast.error('No files to save');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/admin/test-jobs/${jobId}/revised-files`,
        { job_id: jobId, files: revisedFiles, revision_notes: revisionNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setRevisedFiles([]);
      setRevisionNotes('');
      fetchJobDetail();
    } catch (error) {
      toast.error('Failed to save revised files');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'In Review': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Revision Requested': 'bg-orange-100 text-orange-800',
      'Revision Delivered': 'bg-teal-100 text-teal-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getClientStatusColor = (status) => {
    const colors = {
      'Approved': 'bg-green-100 text-green-800',
      'Needs Revision': 'bg-orange-100 text-orange-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-zinc-100 text-zinc-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zinc-900"></div>
        </div>
      </AdminLayout>
    );
  }

  const showRevisionUpload = job?.client_status === 'Needs Revision';

  return (
    <AdminLayout>
      <div data-testid="admin-job-detail" className="space-y-8">
        {/* Header */}
        <div>
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/admin/test-jobs')}
            variant="ghost"
            className="mb-4 text-zinc-600 hover:text-zinc-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Test Jobs
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Job Details
              </h1>
              <p className="text-zinc-600 mt-1">ID: {job.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                {job.status}
              </span>
              {job.client_status && (
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getClientStatusColor(job.client_status)}`}>
                  Client: {job.client_status}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Client Feedback Alert */}
        {job.client_status && (
          <div className={`p-4 rounded-lg border ${
            job.client_status === 'Approved' ? 'bg-green-50 border-green-200' :
            job.client_status === 'Needs Revision' ? 'bg-orange-50 border-orange-200' :
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              <MessageSquare className={`w-5 h-5 mt-0.5 ${
                job.client_status === 'Approved' ? 'text-green-600' :
                job.client_status === 'Needs Revision' ? 'text-orange-600' :
                'text-red-600'
              }`} />
              <div>
                <h3 className="font-semibold text-zinc-900">Client Feedback Received</h3>
                <p className="text-sm text-zinc-600 mt-1">
                  <strong>Status:</strong> {job.client_status}
                </p>
                {job.client_feedback && (
                  <p className="text-sm text-zinc-700 mt-2 bg-white/50 p-3 rounded">
                    &ldquo;{job.client_feedback}&rdquo;
                  </p>
                )}
                {job.client_feedback_at && (
                  <p className="text-xs text-zinc-500 mt-2">
                    Submitted on {formatDate(job.client_feedback_at)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Job Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Information */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Client Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="text-sm text-zinc-600">Name</p>
                    <p className="text-base font-medium text-zinc-900">{job.client_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="text-sm text-zinc-600">Email</p>
                    <p className="text-base font-medium text-zinc-900">{job.client_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="text-sm text-zinc-600">Submitted</p>
                    <p className="text-base font-medium text-zinc-900">{formatDate(job.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Client Notes */}
            {job.notes && (
              <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Client Notes
                </h2>
                <p className="text-zinc-700 whitespace-pre-wrap">{job.notes}</p>
              </div>
            )}

            {/* Uploaded Files */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileText className="w-5 h-5" />
                Original Submission
              </h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {job.files && job.files.length > 0 ? (
                  job.files.map((file, index) => (
                    <div
                      key={index}
                      data-testid={`file-item-${index}`}
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Folder className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-zinc-900 truncate" title={file.relative_path || file.file_name}>
                            {file.relative_path || file.file_name}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {(file.file_size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        data-testid={`download-file-${index}`}
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => downloadFile(file, index, 'original')}
                        disabled={downloading[`original-${index}`]}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        {downloading[`original-${index}`] ? '...' : 'Download'}
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-zinc-500 text-center py-4">No files uploaded</p>
                )}
              </div>
              <div className="pt-3 border-t border-zinc-200 mt-3">
                <p className="text-sm text-zinc-600">
                  Total: {job.files?.length || 0} files ({(job.total_file_size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </div>
            </div>

            {/* Existing Retouched Files */}
            {job.retouched_files && job.retouched_files.length > 0 && (
              <div className="bg-white rounded-xl border border-green-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Retouched Files (Delivered)
                </h2>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {job.retouched_files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Folder className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-zinc-900 truncate" title={file.relative_path || file.file_name}>
                            {file.relative_path || file.file_name}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {(file.file_size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-600 hover:text-green-700 hover:bg-green-100"
                        onClick={() => downloadFile(file, index, 'retouched')}
                        disabled={downloading[`retouched-${index}`]}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        {downloading[`retouched-${index}`] ? '...' : 'Download'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revision History */}
            {job.revisions && job.revisions.length > 0 && (
              <div className="bg-white rounded-xl border border-teal-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <History className="w-5 h-5 text-teal-600" />
                  Revision History ({job.revisions.length})
                </h2>
                <div className="space-y-4">
                  {job.revisions.map((revision, revIndex) => (
                    <div key={revIndex} className="bg-teal-50 rounded-lg border border-teal-100 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-teal-800">
                          Revision #{revision.revision_number}
                        </h3>
                        <span className="text-xs text-teal-600">
                          {formatDate(revision.uploaded_at)}
                        </span>
                      </div>
                      {revision.notes && (
                        <p className="text-sm text-zinc-600 mb-3 bg-white/50 p-2 rounded">
                          Notes: {revision.notes}
                        </p>
                      )}
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {revision.files.map((file, fileIndex) => (
                          <div
                            key={fileIndex}
                            className="flex items-center justify-between p-2 bg-white rounded border border-teal-100"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Folder className="w-4 h-4 text-teal-600 flex-shrink-0" />
                              <span className="text-sm text-zinc-900 truncate" title={file.relative_path || file.file_name}>
                                {file.relative_path || file.file_name}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-teal-600 hover:text-teal-700 h-7 px-2"
                              onClick={() => downloadFile(file, `${revIndex}-${fileIndex}`, 'revision')}
                              disabled={downloading[`revision-${revIndex}-${fileIndex}`]}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Retouched Files Section */}
            {!showRevisionUpload && (
              <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Upload className="w-5 h-5 text-blue-600" />
                  Upload Retouched Files
                </h2>
                
                {/* Hidden inputs */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleRetouchedFileSelect}
                  className="hidden"
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  multiple
                  onChange={handleRetouchedFileSelect}
                  className="hidden"
                />
                
                {/* Drop Zone */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleRetouchedDrop}
                  className="border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                >
                  <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                  <p className="text-sm text-zinc-600 mb-3">
                    Drag & drop files or folders here
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingRetouched}
                    >
                      <File className="w-4 h-4 mr-2" />
                      Select Files
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => folderInputRef.current?.click()}
                      disabled={uploadingRetouched}
                    >
                      <Folder className="w-4 h-4 mr-2" />
                      Select Folder
                    </Button>
                  </div>
                </div>

                {/* Upload Progress */}
                {uploadingRetouched && Object.keys(uploadProgress).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {Object.entries(uploadProgress).map(([fileName, progress]) => (
                      <div key={fileName} className="flex items-center gap-2 text-xs">
                        <div className="flex-1 truncate text-zinc-600">{fileName}</div>
                        <div className="w-20 bg-zinc-200 rounded-full h-1.5">
                          <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="w-8 text-right text-zinc-500">{progress}%</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Staged Files */}
                {retouchedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-zinc-700 mb-2">
                      Ready to deliver ({retouchedFiles.length} files):
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {retouchedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm p-2 bg-blue-50 rounded">
                          <CheckCircle2 className="w-4 h-4 text-blue-600" />
                          <span className="truncate flex-1">{file.relative_path || file.file_name}</span>
                          <span className="text-xs text-zinc-500">{(file.file_size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={saveRetouchedFiles}
                      disabled={updating}
                      className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {updating ? 'Saving...' : 'Deliver Files to Client'}
                    </Button>
                    <p className="text-xs text-zinc-500 mt-2 text-center">
                      Client will receive an email notification.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Upload Revised Files Section - Only shown when client requests revision */}
            {showRevisionUpload && (
              <div className="bg-white rounded-xl border border-orange-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <RefreshCw className="w-5 h-5 text-orange-600" />
                  Upload Revised Files
                </h2>
                <p className="text-sm text-orange-700 mb-4 bg-orange-50 p-3 rounded">
                  Client requested revision. Upload the revised files below.
                </p>
                
                {/* Hidden inputs */}
                <input
                  ref={revisedFileInputRef}
                  type="file"
                  multiple
                  onChange={handleRevisedFileSelect}
                  className="hidden"
                />
                <input
                  ref={revisedFolderInputRef}
                  type="file"
                  webkitdirectory=""
                  multiple
                  onChange={handleRevisedFileSelect}
                  className="hidden"
                />
                
                {/* Drop Zone */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleRevisedDrop}
                  className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center hover:border-orange-400 hover:bg-orange-50/50 transition-colors"
                >
                  <RefreshCw className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                  <p className="text-sm text-zinc-600 mb-3">
                    Drag & drop revised files or folders here
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => revisedFileInputRef.current?.click()}
                      disabled={uploadingRevised}
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      <File className="w-4 h-4 mr-2" />
                      Select Files
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => revisedFolderInputRef.current?.click()}
                      disabled={uploadingRevised}
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      <Folder className="w-4 h-4 mr-2" />
                      Select Folder
                    </Button>
                  </div>
                </div>

                {/* Upload Progress */}
                {uploadingRevised && Object.keys(revisedUploadProgress).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {Object.entries(revisedUploadProgress).map(([fileName, progress]) => (
                      <div key={fileName} className="flex items-center gap-2 text-xs">
                        <div className="flex-1 truncate text-zinc-600">{fileName}</div>
                        <div className="w-20 bg-zinc-200 rounded-full h-1.5">
                          <div className="bg-orange-600 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="w-8 text-right text-zinc-500">{progress}%</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Revision Notes */}
                <div className="mt-4">
                  <Label className="text-sm font-medium mb-2 block">Revision Notes (optional)</Label>
                  <Input
                    value={revisionNotes}
                    onChange={(e) => setRevisionNotes(e.target.value)}
                    placeholder="Add notes about the revisions made..."
                    className="mb-3"
                  />
                </div>

                {/* Staged Files */}
                {revisedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-zinc-700 mb-2">
                      Ready to deliver ({revisedFiles.length} revised files):
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {revisedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm p-2 bg-orange-50 rounded">
                          <CheckCircle2 className="w-4 h-4 text-orange-600" />
                          <span className="truncate flex-1">{file.relative_path || file.file_name}</span>
                          <span className="text-xs text-zinc-500">{(file.file_size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={saveRevisedFiles}
                      disabled={updating}
                      className="w-full mt-4 bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {updating ? 'Saving...' : 'Deliver Revised Files'}
                    </Button>
                    <p className="text-xs text-zinc-500 mt-2 text-center">
                      Client will receive an email notification about the revision.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Status & Notes */}
          <div className="space-y-6">
            {/* Update Status */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Update Status
              </h2>
              <div className="space-y-4">
                <div>
                  <Label data-testid="status-label" className="text-sm font-medium mb-2 block">Job Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="status-select" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="In Review">In Review</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Revision Requested">Revision Requested</SelectItem>
                      <SelectItem value="Revision Delivered">Revision Delivered</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="update-status-btn"
                  onClick={updateStatus}
                  disabled={updating || status === job.status}
                  className="w-full h-11 bg-zinc-900 text-white hover:bg-zinc-800"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {updating ? 'Updating...' : 'Update Status'}
                </Button>
                <p className="text-xs text-zinc-500">
                  Client will receive an email notification.
                </p>
              </div>
            </div>

            {/* Admin Notes */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Admin Notes
              </h2>
              <div className="space-y-4">
                <div>
                  <Label data-testid="admin-notes-label" className="text-sm font-medium mb-2 block">Internal Notes</Label>
                  <Textarea
                    data-testid="admin-notes-input"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes (not visible to client)..."
                    className="min-h-[120px]"
                  />
                </div>
                <Button
                  data-testid="save-notes-btn"
                  onClick={saveAdminNotes}
                  disabled={updating}
                  variant="outline"
                  className="w-full h-11"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {updating ? 'Saving...' : 'Save Notes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminJobDetail;
